import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Code, CreditCard, Globe, Palette, Link as LinkIcon, Cloud, 
  Shield, Zap, MessageSquare, Building, Wrench, ArrowRight, Check 
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      Code: Code,
      CreditCard: CreditCard,
      Globe: Globe,
      Palette: Palette,
      Link: LinkIcon,
      Cloud: Cloud,
      Shield: Shield,
      Zap: Zap,
      MessageSquare: MessageSquare,
      Building: Building,
      Wrench: Wrench,
    };
    const IconComponent = icons[iconName] || Code;
    return <IconComponent size={28} />;
  };

  const categories = [
    { id: 'all', name: 'All Services' },
    { id: 'development', name: 'Development' },
    { id: 'fintech', name: 'Fintech' },
    { id: 'web', name: 'Web Design' },
    { id: 'design', name: 'UI/UX' },
    { id: 'integration', name: 'Integration' },
    { id: 'hosting', name: 'Hosting' },
    { id: 'security', name: 'Security' },
    { id: 'automation', name: 'Automation' },
    { id: 'communication', name: 'Communication' },
    { id: 'custom', name: 'Custom' },
    { id: 'support', name: 'Support' },
  ];

  const filteredServices = activeCategory === 'all'
    ? services
    : services.filter(s => s.category === activeCategory);

  // Tetris grid layout classes
  const getGridSpan = (index) => {
    const patterns = [
      'md:col-span-4 lg:col-span-4',
      'md:col-span-4 lg:col-span-4',
      'md:col-span-4 lg:col-span-4',
      'md:col-span-6 lg:col-span-6',
      'md:col-span-6 lg:col-span-6',
      'md:col-span-4 lg:col-span-4',
      'md:col-span-4 lg:col-span-4',
      'md:col-span-4 lg:col-span-4',
      'md:col-span-6 lg:col-span-6',
      'md:col-span-6 lg:col-span-6',
      'md:col-span-12 lg:col-span-12',
    ];
    return patterns[index % patterns.length];
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section
        data-testid="services-hero"
        className="relative py-24 lg:py-32 hero-bg"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1771011726574-60ab6028724a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBibHVlJTIwdGVjaG5vbG9neSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc0NjI5Mzg0fDA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="hero-overlay absolute inset-0" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block text-[#FF5E1A] text-xs font-bold tracking-[0.2em] uppercase mb-6">
              Our Services
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6">
              Comprehensive Digital Solutions
            </h1>
            <p className="text-lg text-[#A1B0CC] leading-relaxed">
              From custom software development to fintech integrations, we offer a full 
              range of services to meet your digital transformation needs.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-[#0A1121] border-b border-white/5 sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                data-testid={`filter-${category.id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeCategory === category.id
                    ? 'bg-[#FF5E1A] text-white'
                    : 'bg-[#050B14] text-[#A1B0CC] hover:text-white border border-white/10'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid - Tetris Style */}
      <section data-testid="services-grid" className="py-24 bg-[#050B14]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {filteredServices.map((service, index) => (
              <div
                key={service.id}
                data-testid={`service-card-${index}`}
                className={`bg-[#0A1121] border border-white/5 rounded-2xl p-8 card-hover ${getGridSpan(index)}`}
              >
                <div className="w-14 h-14 bg-[#FF5E1A]/10 rounded-xl flex items-center justify-center text-[#FF5E1A] mb-6">
                  {getIcon(service.icon)}
                </div>
                <h3 className="text-white font-bold text-xl mb-3">{service.title}</h3>
                <p className="text-[#A1B0CC] text-sm leading-relaxed mb-6">
                  {service.description}
                </p>
                {service.features && service.features.length > 0 && (
                  <ul className="space-y-2">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-[#A1B0CC] text-sm">
                        <Check size={16} className="text-[#FF5E1A] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section data-testid="process-section" className="py-24 bg-[#0A1121]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-[#FF5E1A] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
              Our Process
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
              How We Work
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', desc: 'Understanding your business needs and goals' },
              { step: '02', title: 'Planning', desc: 'Creating detailed technical specifications' },
              { step: '03', title: 'Development', desc: 'Building your solution with best practices' },
              { step: '04', title: 'Delivery', desc: 'Deployment, training, and ongoing support' },
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="text-5xl font-bold text-[#FF5E1A]/20 mb-4">{item.step}</div>
                <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-[#A1B0CC] text-sm">{item.desc}</p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 right-0 w-1/2 border-t border-dashed border-white/10" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#050B14]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="bg-gradient-to-r from-[#FF5E1A]/20 to-transparent border border-[#FF5E1A]/20 rounded-3xl p-12 lg:p-16 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Need a Custom Solution?
            </h2>
            <p className="text-[#A1B0CC] max-w-2xl mx-auto mb-8">
              Every business is unique. Let&apos;s discuss how we can tailor our services 
              to meet your specific requirements.
            </p>
            <Link
              to="/contact"
              data-testid="services-cta"
              className="inline-flex items-center gap-2 bg-[#FF5E1A] hover:bg-[#FF7A33] text-white font-bold rounded-full px-8 py-4 transition-all btn-glow"
            >
              Get a Free Consultation <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
